// @flow
/* This isn't a public component - disable linter rules for public components */
/* eslint-disable ta-code-conventions/has-matching-test,ta-code-conventions/has-matching-storydoc */
import React from 'react';
import classNames from '@ta/common.classnames';
import { Avatar, type AvatarPhoto } from '@ta/styleguide.avatar';
import { featureIsEnabled } from '@ta/common.features';
import { localize, unescaped } from '@ta/common.i18n';
import { escapeHtml } from '@ta/common.text-html';
import { Icon } from '@ta/styleguide.icon';
import { Link } from '@ta/common.routing';
import ObfuscatedLink from '@ta/common.obfuscated-link';
import { InteractionLogger } from '@ta/tracking.interactions';
import type { MemberProfile, LocationInformation } from './types';
import styles from './SponsoredHomepageHeroInfo.less';

type Props = {|
  profileAvatar: ?AvatarPhoto,
  profile: ?MemberProfile,
  destination: ?LocationInformation,
  url: ?string,
|};

/**
 * Dispays the destination geo of the sponsored hero as well as the sponsor profile.
 *
 * This combines the information in both GeoPin and SponsoredProfile into a single UI element.
 */
const SponsoredHomepageHeroInfo = ({ profileAvatar, profile, destination, url }: Props) => {
  if (!profile || !destination) {
    return null;
  }

  let sponsorLink;
  if (url) {
    const encodedUrl = url;
    sponsorLink = (
      <InteractionLogger>
        {logInteraction => (
          <ObfuscatedLink
            className={styles.link}
            encodedUrl={encodedUrl}
            target="_blank"
            onClick={e => logInteraction(e, 'heroProfileClick')}
          >
            {localize('insp_sponby') /* Sponsored by */}{' '}
            <span className={styles.value}>
              {profile.displayName}
              <Icon name="external-link-no-box" className={styles.external} />
            </span>
          </ObfuscatedLink>
        )}
      </InteractionLogger>
    );
  } else {
    const profileUrl = `/Profile/${profile.username}`;
    sponsorLink = (
      <InteractionLogger>
        {logInteraction => (
          <Link
            className={styles.link}
            href={profileUrl}
            target="_blank"
            onClick={e => logInteraction(e, 'heroProfileClick')}
          >
            {localize('insp_sponby') /* Sponsored by */}{' '}
            <span className={styles.value}>
              {profile.displayName}
              <Icon name="verified-fill" className={styles.verified} />
              <Icon name="checkmark" className={styles.checkmark} />
            </span>
          </Link>
        )}
      </InteractionLogger>
    );
  }
  const urlBase = featureIsEnabled('navigation_to_scoped_home') ? '/Home' : '/Tourism';
  const geoName = destination.additionalNamesMap?.LONG || destination.additionalNames?.long;
  const geoLink = (
    <InteractionLogger>
      {logInteraction => (
        <Link
          className={styles.link}
          href={`${urlBase}-g${destination.locationId}`}
          target="_blank"
          onClick={e => logInteraction(e, 'heroGeoPinClick')}
        >
          {unescaped(
            // Photo of {geo}
            localize('homepage_hero_sponsor_short', {
              geo: `<span class="${styles.value}">${escapeHtml(geoName)}</span>`,
            }),
          )}
        </Link>
      )}
    </InteractionLogger>
  );

  return (
    <div className={styles.sponsoredHomepageHeroInfo}>
      <div className={classNames(styles.avatar, 'is-hidden-mobile')}>
        <Avatar key={profile.id} photo={profileAvatar || profile.avatar} notLazy />
      </div>
      <div className={styles.text}>
        {geoLink}
        {sponsorLink}
      </div>
    </div>
  );
};

export default SponsoredHomepageHeroInfo;
